import { generateUuid } from '@/utils/generate-uuid.ts';
import { readStoredString, readVersionedJson, writeStoredString, writeVersionedJson, type StorageProvider } from '@/utils/browser-storage.ts';

const attributionKey = 'vennevent:launch-attribution';
const sessionKey = 'vennevent:launch-session';
const allowedPattern = /[^a-z0-9._-]/g;

export type LaunchAttribution = { source: string; campaign: string };
export type LaunchEventType = 'organizer_started' | 'invitation_sent';

function attributionOptions(storage: StorageProvider = () => window.localStorage) {
  return {
    key: attributionKey,
    version: 1,
    storage,
    maxBytes: 512,
    validate(value: unknown): value is LaunchAttribution {
      if (!value || typeof value !== 'object') return false;
      const candidate = value as Partial<LaunchAttribution>;
      return (
        typeof candidate.source === 'string' &&
        candidate.source === clean(candidate.source) &&
        typeof candidate.campaign === 'string' &&
        candidate.campaign === clean(candidate.campaign)
      );
    },
    migrateLegacy(value: unknown): LaunchAttribution | undefined {
      if (!value || typeof value !== 'object') return undefined;
      const candidate = value as Partial<LaunchAttribution>;
      return { source: clean(candidate.source), campaign: clean(candidate.campaign) };
    },
  };
}

function clean(value: unknown) {
  const text = typeof value === 'string' || typeof value === 'number' ? String(value) : '';
  return text.trim().toLowerCase().replace(allowedPattern, '').slice(0, 80);
}

export function captureLaunchAttribution(search: string): LaunchAttribution {
  const params = new URLSearchParams(search);
  const incoming = {
    source: clean(params.get('utm_source') || params.get('source')),
    campaign: clean(params.get('utm_campaign') || params.get('campaign')),
  };
  if (incoming.source || incoming.campaign) writeVersionedJson(attributionOptions(), incoming);
  return readLaunchAttribution();
}

export function readLaunchAttribution(): LaunchAttribution {
  return readVersionedJson(attributionOptions()) ?? { source: '', campaign: '' };
}

function getLaunchSession() {
  const options = {
    key: sessionKey,
    storage: () => window.sessionStorage,
    maxBytes: 80,
    validate: (candidate: string) => candidate.length > 0 && candidate === clean(candidate),
  };
  let value = readStoredString(options);
  if (!value) {
    value = generateUuid().replace(/-/g, '');
    writeStoredString(options, value);
  }
  return value;
}

export async function trackLaunchEvent(eventType: LaunchEventType, target: { groupId?: string; eventId?: string } = {}) {
  const { usePB } = await import('@/composables/database/use-pocket-base.ts');
  const attribution = readLaunchAttribution();
  await usePB().db.send('/api/custom/launch/track', {
    method: 'POST',
    body: {
      event_type: eventType,
      group_id: target.groupId || '',
      event_id: target.eventId || '',
      source: attribution.source,
      campaign: attribution.campaign,
      session_key: getLaunchSession(),
    },
  });
}

export function withLaunchAttribution(url: string, source: string, campaign = '') {
  const target = new URL(url, window.location.origin);
  target.searchParams.set('source', clean(source));
  if (campaign) target.searchParams.set('campaign', clean(campaign));
  return target.toString();
}
